import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";

import { localCartSaleFingerprint, readLocalCart, type LocalCartDraft } from "../runtime/local-store/cart-state";
import { readTerminalCredential } from "../runtime/local-store/terminal-state";

type RuntimeSnapshot = {
  cart: LocalCartDraft | null;
};

type LinePromotionPresentation = {
  discount: number;
  promotionNames: string[];
};

function formatMoney(value: number, currencyCode: string) {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(value);
}

export function AppliedPromotionSummaryHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ cart: null });

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const terminal = await readTerminalCredential();
      const identity = terminal?.identity ?? null;
      return {
        cart: identity ? await readLocalCart(identity) : null,
      } satisfies RuntimeSnapshot;
    }).subscribe({
      next: setRuntime,
      error: () => setRuntime({ cart: null }),
    });

    return () => subscription.unsubscribe();
  }, []);

  const freshQuote = useMemo(() => {
    const cart = runtime.cart;
    if (!cart?.quote || cart.quote.cartFingerprint !== localCartSaleFingerprint(cart)) return null;
    return cart.quote;
  }, [runtime.cart]);

  const appliedPromotions = freshQuote?.appliedPromotions ?? [];

  const label = useMemo(() => {
    if (appliedPromotions.length === 0) return "";
    const names = appliedPromotions.map((promotion) => promotion.policyName).filter(Boolean);
    const primaryName = names[0] || "โปรโมชั่น";
    return appliedPromotions.length === 1
      ? `ใช้โปร: ${primaryName}`
      : `ใช้โปร: ${primaryName} +อีก ${appliedPromotions.length - 1}`;
  }, [appliedPromotions]);

  const linePresentations = useMemo(() => {
    const result = new Map<string, LinePromotionPresentation>();
    for (const promotion of appliedPromotions) {
      for (const allocation of promotion.lineAllocations ?? []) {
        if (!allocation.lineId || !Number.isFinite(allocation.amount) || allocation.amount <= 0) continue;
        const current = result.get(allocation.lineId) ?? { discount: 0, promotionNames: [] };
        current.discount += allocation.amount;
        if (promotion.policyName && !current.promotionNames.includes(promotion.policyName)) {
          current.promotionNames.push(promotion.policyName);
        }
        result.set(allocation.lineId, current);
      }
    }
    return result;
  }, [appliedPromotions]);

  useEffect(() => {
    let activeSummaryRow: HTMLElement | null = null;

    const clearLinePresentation = () => {
      document.querySelectorAll<HTMLElement>(".sale-cart-line__copy[data-promotion-line]").forEach((node) => {
        node.removeAttribute("data-promotion-line");
      });
      document.querySelectorAll<HTMLElement>(".sale-cart-line__amount[data-promotion-net]").forEach((node) => {
        node.removeAttribute("data-promotion-net");
        node.removeAttribute("aria-label");
      });
    };

    const applyPresentation = () => {
      const summary = document.querySelector<HTMLElement>(".sale-cart-summary");
      const candidate = summary?.children.item(1);
      const row = candidate instanceof HTMLElement ? candidate : null;

      if (activeSummaryRow && activeSummaryRow !== row) activeSummaryRow.removeAttribute("data-applied-promotion");
      activeSummaryRow = row;
      if (row) {
        if (label) {
          row.setAttribute("data-applied-promotion", label);
          row.setAttribute("aria-label", `ส่วนลด / โปรโมชั่น ${label}`);
        } else {
          row.removeAttribute("data-applied-promotion");
          row.removeAttribute("aria-label");
        }
      }

      // Important: this host is presentation-only. It must never rewrite the financial
      // amount text rendered by PosWorkspace. The workspace owns the current optimistic
      // cart state, while this host reads persisted IndexedDB state and may legitimately
      // trail by one local write. Letting both write totals caused stale payable amounts.
      clearLinePresentation();
      const cart = runtime.cart;
      if (!freshQuote || !cart || linePresentations.size === 0) return;

      const shells = Array.from(document.querySelectorAll<HTMLElement>(".sale-cart-line-shell"));
      cart.lines.forEach((line, index) => {
        const presentation = linePresentations.get(line.lineId);
        const shell = shells[index];
        if (!presentation || !shell) return;

        const copy = shell.querySelector<HTMLElement>(".sale-cart-line__copy");
        const amount = shell.querySelector<HTMLElement>(".sale-cart-line__amount");
        if (!copy || !amount) return;

        const gross = line.unitPrice * line.quantity;
        const net = Math.max(0, gross - presentation.discount);
        const promotionLabel = presentation.promotionNames.length === 1
          ? presentation.promotionNames[0]
          : presentation.promotionNames.length > 1
            ? `${presentation.promotionNames[0]} +อีก ${presentation.promotionNames.length - 1}`
            : "โปรโมชั่น";
        const discountLabel = `โปร: ${promotionLabel} −${formatMoney(presentation.discount, line.currencyCode)}`;
        const netLabel = `หลังโปร ${formatMoney(net, line.currencyCode)}`;

        copy.setAttribute("data-promotion-line", discountLabel);
        amount.setAttribute("data-promotion-net", netLabel);
        amount.setAttribute(
          "aria-label",
          `ยอดก่อนโปรโมชั่น ${formatMoney(gross, line.currencyCode)} ${discountLabel} ${netLabel}`,
        );
      });
    };

    applyPresentation();
    const observer = new MutationObserver(applyPresentation);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      activeSummaryRow?.removeAttribute("data-applied-promotion");
      activeSummaryRow?.removeAttribute("aria-label");
      clearLinePresentation();
    };
  }, [freshQuote, label, linePresentations, runtime.cart]);

  return null;
}
